import { memo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { AlertTriangle, Pencil } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency } from "@/shared/lib/formatters";
import { RentalType } from "@/shared/types/enums";
import { formatDatetimeLocalLabel } from "@/shared/lib/date-format";
import type { RentalCreateFormSchema } from "../../rental-create-schema";
import { StepHeading } from "./StepHeading";

interface StepReviewProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  sellerLabel: string | null;
  selectedClientLabel: string | null;
  selectedContractLabel: string | null;
  parentContractLabel: string | null;
  /** Volta para uma etapa específica para edição (índice do passo). */
  onEditStep: (index: number) => void;
  /**
   * Formata os valores de período. Default: `datetime-local` (fluxo USER).
   * O fluxo ADMIN usa datas sem hora e passa o formatador correspondente.
   */
  formatPeriodLabel?: (value: string) => string | null;
  /** O fluxo ADMIN não tem contrato pai. */
  showParentContract?: boolean;
  /** Exibe as datas efetivas do aluguel retroativo (exclusivo do ADMIN). */
  showRetroactive?: boolean;
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Crédito",
  DEBIT_CARD: "Débito",
  CASH: "Dinheiro",
};

export const StepReview = memo(function StepReview({
  form,
  sellerLabel,
  selectedClientLabel,
  selectedContractLabel,
  parentContractLabel,
  onEditStep,
  formatPeriodLabel = formatDatetimeLocalLabel,
  showParentContract = true,
  showRetroactive = false,
}: StepReviewProps) {
  const control = form.control;
  const values = {
    rentalType: useWatch({ control, name: "rentalType" }),
    newClient: useWatch({ control, name: "newClient" }),
    newContract: useWatch({ control, name: "newContract" }),
    expectedOutAt: useWatch({ control, name: "expectedOutAt" }),
    expectedInAt: useWatch({ control, name: "expectedInAt" }),
    stockIds: useWatch({ control, name: "stockIds" }),
    gownItems: useWatch({ control, name: "gownItems" }),
    hasShoes: useWatch({ control, name: "hasShoes" }),
    shoesPairQuantity: useWatch({ control, name: "shoesPairQuantity" }),
    totalValue: useWatch({ control, name: "totalValue" }),
    payments: useWatch({ control, name: "payments" }),
    description: useWatch({ control, name: "description" }),
    emitInvoice: useWatch({ control, name: "emitInvoice" }),
    isRetroactive: useWatch({ control, name: "isRetroactive" }),
    outAt: useWatch({ control, name: "outAt" }),
    inAt: useWatch({ control, name: "inAt" }),
  };
  const isSuit = values.rentalType === RentalType.SUIT;

  const clientValue =
    selectedClientLabel ?? values.newClient?.name ?? "Não definido";
  const contractValue =
    selectedContractLabel ??
    (values.newContract
      ? `Nº ${values.newContract.contractNumber}`
      : "Não definido");

  const out = formatPeriodLabel(values.expectedOutAt) ?? "—";
  const back = formatPeriodLabel(values.expectedInAt) ?? "—";

  const itemCount = isSuit ? values.stockIds.length : values.gownItems.length;
  const hasItems = itemCount > 0;

  const paidTotal = values.payments.reduce((sum, p) => sum + p.paidValue, 0);
  const remaining = values.totalValue - paidTotal;

  return (
    <div className="space-y-6">
      <StepHeading
        title="Revisão"
        description="Confira os dados antes de salvar."
      />

      <ReviewSection title="Tipo e vendedor" onEdit={() => onEditStep(0)}>
        <ReviewRow
          label="Tipo"
          value={isSuit ? "Aluguel de Terno" : "Aluguel de Beca"}
        />
        <ReviewRow label="Vendedor" value={sellerLabel ?? "Não definido"} />
      </ReviewSection>

      <ReviewSection title="Cliente e contrato" onEdit={() => onEditStep(1)}>
        <ReviewRow label="Cliente" value={clientValue} />
        <ReviewRow label="Contrato" value={contractValue} />
        {showParentContract && parentContractLabel ? (
          <ReviewRow label="Contrato pai" value={parentContractLabel} />
        ) : null}
      </ReviewSection>

      <ReviewSection title="Período" onEdit={() => onEditStep(2)}>
        <ReviewRow label="Retirada prevista" value={out} />
        <ReviewRow label="Devolução prevista" value={back} />
      </ReviewSection>

      {showRetroactive && values.isRetroactive ? (
        <ReviewSection title="Aluguel retroativo" onEdit={() => onEditStep(2)}>
          <ReviewRow
            label="Saída efetiva"
            value={formatPeriodLabel(values.outAt ?? "") ?? "—"}
          />
          <ReviewRow
            label="Retorno efetivo"
            value={formatPeriodLabel(values.inAt ?? "") ?? "—"}
          />
        </ReviewSection>
      ) : null}

      <ReviewSection
        title={isSuit ? "Peças" : "Itens de beca"}
        onEdit={() => onEditStep(3)}
      >
        {hasItems ? (
          <ReviewRow
            label={isSuit ? "Peças adicionadas" : "Itens adicionados"}
            value={String(itemCount)}
          />
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Nenhum{isSuit ? "a peça" : " item"} adicionad
              {isSuit ? "a" : "o"}. O aluguel será salvo{" "}
              <strong>incompleto</strong> e poderá ser completado depois.
            </p>
          </div>
        )}
        {isSuit && values.hasShoes ? (
          <ReviewRow
            label="Sapatos"
            value={`Sim · ${values.shoesPairQuantity} par(es)`}
          />
        ) : null}
      </ReviewSection>

      <ReviewSection title="Valores e pagamentos" onEdit={() => onEditStep(4)}>
        <ReviewRow
          label="Valor total"
          value={formatCurrency(values.totalValue)}
        />
        {values.payments.length > 0 ? (
          <>
            <ul className="space-y-1">
              {values.payments.map((p, i) => (
                <li
                  key={i}
                  className="flex justify-between text-sm text-muted-foreground"
                >
                  <span>
                    {PAYMENT_METHOD_LABEL[p.paymentMethod] ?? p.paymentMethod}
                  </span>
                  <span>{formatCurrency(p.paidValue)}</span>
                </li>
              ))}
            </ul>
            <ReviewRow label="Total pago" value={formatCurrency(paidTotal)} />
            {remaining > 0 ? (
              <ReviewRow label="Restante" value={formatCurrency(remaining)} />
            ) : null}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            Nenhum pagamento registrado.
          </p>
        )}
        {values.description.trim() ? (
          <ReviewRow label="Observações" value={values.description} />
        ) : null}
        <ReviewRow
          label="Nota fiscal"
          value={values.emitInvoice ? "Será emitida" : "Não será emitida"}
        />
      </ReviewSection>
    </div>
  );
});

interface ReviewSectionProps {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}

function ReviewSection({ title, onEdit, children }: ReviewSectionProps) {
  return (
    <div className="rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="gap-1.5 text-muted-foreground"
        >
          <Pencil className="w-3.5 h-3.5" />
          Editar
        </Button>
      </div>
      <div className="px-4 py-3 space-y-2">{children}</div>
    </div>
  );
}

interface ReviewRowProps {
  label: string;
  value: string;
}

function ReviewRow({ label, value }: ReviewRowProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right break-words">
        {value}
      </span>
    </div>
  );
}
